import React, { useEffect, useState } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Select,
  useTheme,
  CircularProgress,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  Tabs,
  Tab,
  Badge,
  useMediaQuery,
} from "@mui/material";
import {
  CalendarToday,
  LocalPharmacy,
  MedicalServices,
  Science,
  Build,
  AccountBalanceWallet,
  Undo,
  TrendingUp,
  Favorite,
  Healing,
  Close,
  FilterList,
  History,
  Event,
  Paid,
  Description,
  ArrowForward,
  Receipt,
  ShowChart,
  MoneyOff,
  SettingsBackupRestore,
  PointOfSale,
  Payments,
  Savings,
  AccountTree,
  Summarize,
  DateRange,
  Today,
  AllInclusive,
  Person,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  format,
  subDays,
  startOfMonth,
  startOfYear,
  isAfter,
  isValid,
} from "date-fns";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
const formatCurrency = (value, currency = "AFN") => {
  if (isNaN(value)) return "N/A";
  // Handle invalid currency codes
  const validCurrency =
    currency && ["AFN", "USD", "EUR"].includes(currency) ? currency : "AFN";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: validCurrency,
      minimumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    // Fallback to simple formatting if NumberFormat fails
    const symbol =
      validCurrency === "USD" ? "$" : validCurrency === "EUR" ? "€" : "؋";
    return `${symbol}${Number(value).toLocaleString()}`;
  }
};
const formatCurrencyWithSpace = (value, currency = "AFN") => {
  if (isNaN(value)) return "N/A";
  // Handle invalid currency codes
  const validCurrency =
    currency && ["AFN", "USD", "EUR"].includes(currency) ? currency : "AFN";
  let symbol = "؋";
  if (validCurrency === "USD") symbol = "$";
  else if (validCurrency === "EUR") symbol = "€";
  else if (validCurrency === "AFN") symbol = "؋";
  return `${symbol}\u00A0${Number(value).toLocaleString()}`;
};
const StatCard = ({
  icon,
  title,
  value,
  amount,
  onClick,
  theme,
  history,
  loading = false,
  highlight,
  cardSx,
}) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Card sx={{ height: "100%", p: 2, borderRadius: 3, boxShadow: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={180} />
      </Card>
    );
  }
  // Capitalize each word in the title
  const capitalizeTitle = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());
  // Helper to render amount with minus sign if needed (for Net Income summary card)
  const renderAmount = (amt, isNetIncome, idx) => {
    // Only for Net Income summary card
    if (!isNetIncome)
      return (
        <Typography
          key={idx}
          variant={idx === 0 ? "h4" : "body1"}
          fontWeight="bold"
          sx={{
            color: cardSx?.color || theme.palette.primary.contrastText,
            lineHeight: 1.2,
            mt: idx > 0 ? 1 : 0,
            fontSize: isSummaryCard ? 32 : undefined,
            textShadow: isSummaryCard
              ? "0 2px 8px rgba(0,0,0,0.10)"
              : undefined,
          }}
        >
          {amt}
        </Typography>
      );
    // For Net Income, check for minus at start or end
    let isNegative = false;
    let absAmt = amt;
    if (typeof amt === "string") {
      // Remove any minus sign at start or end and move it to the front
      const trimmed = amt.trim();
      if (trimmed.endsWith("-")) {
        isNegative = true;
        absAmt = trimmed.slice(0, -1).trim();
      } else if (trimmed.startsWith("-")) {
        isNegative = true;
        absAmt = trimmed.slice(1).trim();
      }
    }
    return (
      <Typography
        key={idx}
        variant={idx === 0 ? "h4" : "body1"}
        fontWeight="bold"
        sx={{
          color: cardSx?.color || theme.palette.primary.contrastText,
          lineHeight: 1.2,
          mt: idx > 0 ? 1 : 0,
          display: "flex",
          alignItems: "center",
          fontSize: isSummaryCard ? 32 : undefined,
          textShadow: isSummaryCard ? "0 2px 8px rgba(0,0,0,0.10)" : undefined,
        }}
      >
        {isNegative && (
          <span style={{ marginRight: 4, fontWeight: 700, fontSize: 32 }}>
            -
          </span>
        )}
        {absAmt}
      </Typography>
    );
  };
  const isNetIncomeSummary =
    title && title.toLowerCase().includes("net income") && cardSx;
  // --- Improved summary card design ---
  const isSummaryCard = !!cardSx;
  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: history?.length > 0 ? "pointer" : "default",
        transition: "all 0.3s ease",
        border: 0,
        borderRadius: 4,
        boxShadow: isSummaryCard ? "0 8px 32px 0 rgba(31, 38, 135, 0.15)" : 2,
        p: isSummaryCard ? 4 : 2,
        background: isSummaryCard
          ? `linear-gradient(135deg, ${cardSx?.bgcolor} 60%, ${theme.palette.background.paper} 100%)`
          : cardSx?.bgcolor || theme.palette.primary.main,
        color: cardSx?.color || theme.palette.primary.contrastText,
        position: "relative",
        borderTop: isSummaryCard
          ? `3px solid ${cardSx?.borderLeft || cardSx?.bgcolor}`
          : undefined,
        borderBottom: isSummaryCard
          ? `3px solid ${cardSx?.borderLeft || cardSx?.bgcolor}`
          : undefined,
        ...cardSx,
        "&:hover": {
          boxShadow: history?.length > 0 ? theme.shadows[8] : undefined,
          transform:
            history?.length > 0 ? "translateY(-4px) scale(1.03)" : undefined,
        },
      }}
      aria-label={`${title} card`}
      role={history?.length > 0 ? "button" : undefined}
      tabIndex={history?.length > 0 ? 0 : -1}
      onKeyDown={(e) => {
        if (history?.length > 0 && (e.key === "Enter" || e.key === " ")) {
          onClick();
        }
      }}
    >
      {/* Icon in a circle */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: isSummaryCard
            ? cardSx?.borderLeft || cardSx?.bgcolor
            : theme.palette.primary.contrastText,
          color: isSummaryCard
            ? cardSx?.color || "#fff"
            : theme.palette.primary.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 36,
          boxShadow: isSummaryCard ? 3 : 1,
          mb: 2,
        }}
      >
        {icon}
      </Box>
      {/* Title */}
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{
          color: cardSx?.color || theme.palette.primary.contrastText,
          textAlign: "center",
          mb: 1,
          letterSpacing: 1,
          fontSize: 22,
          textShadow: isSummaryCard ? "0 2px 8px rgba(0,0,0,0.10)" : undefined,
        }}
      >
        {capitalizeTitle(title)}
      </Typography>
      {/* Value/Amount */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        {Array.isArray(amount) ? (
          <Box>
            {amount.map((amt, idx) =>
              renderAmount(amt, isNetIncomeSummary, idx)
            )}
          </Box>
        ) : (
          renderAmount(amount, isNetIncomeSummary, 0)
        )}
      </Box>
      {/* History badge: only for non-summary cards */}
      {!isSummaryCard && (
        <Box sx={{ position: "absolute", top: 16, right: 16 }}>
          <Badge
            badgeContent={history?.length || 0}
            color="warning"
            sx={{ mr: 1 }}
          >
            <History
              sx={{
                color: cardSx?.color || theme.palette.primary.contrastText,
                mr: 1,
                fontSize: 22,
              }}
            />
          </Badge>
        </Box>
      )}
      {/* View Details button at the bottom of the card */}
      {!isSummaryCard && history?.length > 0 && (
        <Box
          sx={{
            mt: "auto",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForward />}
            sx={{
              borderRadius: 999,
              px: 3,
              fontWeight: 700,
              background:
                theme.palette.mode === "light"
                  ? theme.palette.grey[200]
                  : theme.palette.grey[800],
              color: theme.palette.text.primary,
              boxShadow: 1,
              textTransform: "none",
              "&:hover": {
                background: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
              },
            }}
            onClick={onClick}
          >
            {t("overview.viewDetails", "View Details")}
          </Button>
        </Box>
      )}
    </Card>
  );
};
const HistoryDialog = ({ open, onClose, history, title }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const formatEmployeeInfo = (employee) => {
    if (!employee) return "-";
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Person fontSize="small" />
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {employee.name || "N/A"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {employee.id || "N/A"}
          </Typography>
        </Box>
      </Box>
    );
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (!isValid(date)) return "-";
      return <Typography variant="body2">{format(date, "PPpp")}</Typography>;
    } catch (error) {
      return "-";
    }
  };
  const formatTime = (timeString) => {
    if (!timeString) return "-";
    try {
      const time = new Date(timeString);
      if (!isValid(time)) return "-";
      return <Typography variant="body2">{format(time, "p")}</Typography>;
    } catch (error) {
      return "-";
    }
  };
  const renderValue = (value, fieldName = "", item = {}) => {
    if (value === null || value === undefined) return "-";
    // Handle boolean values
    if (typeof value === "boolean")
      return value ? t("common.yes", "Yes") : t("common.no", "No");
    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return "-";
      // Special handling for items/drugs arrays
      if (fieldName === "items" || fieldName === "drugs") {
        return (
          <Box sx={{ maxHeight: 120, overflowY: "auto" }}>
            {value.map((subItem, idx) => (
              <Box key={idx} mb={1} p={1} bgcolor="grey.100" borderRadius={1}>
                <Typography variant="body2" fontWeight="bold">
                  {subItem.medicine_name ||
                    subItem.stock_medicine_name ||
                    `Item ${idx + 1}`}
                </Typography>
                <Typography variant="caption" display="block">
                  {t("overview.quantity", "Quantity")}: {subItem.quantity || 0}
                </Typography>
                {subItem.dosage && (
                  <Typography variant="caption" display="block">
                    {t("overview.dosage", "Dosage")}: {subItem.dosage}
                  </Typography>
                )}
                {subItem.price !== undefined && (
                  <Typography variant="caption" display="block">
                    {t("overview.price", "Price")}:{" "}
                    {formatCurrency(subItem.price, item.currency || "AFN")}
                  </Typography>
                )}
                {subItem.total_price !== undefined && (
                  <Typography variant="caption" display="block">
                    {t("overview.total", "Total")}:{" "}
                    {formatCurrency(
                      subItem.total_price,
                      item.currency || "AFN"
                    )}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        );
      }
      return (
        <Tooltip title={value.join(", ")}>
          <Chip
            label={`${value.length} ${t("overview.items", "items")}`}
            size="small"
          />
        </Tooltip>
      );
    }
    // Handle objects
    if (typeof value === "object") {
      // Handle person objects
      if (value.id && value.name) return formatEmployeeInfo(value);
      // Handle currency objects
      if (fieldName === "total" && typeof value === "object") {
        return (
          <Box>
            {Object.entries(value).map(([currency, amount]) => (
              <Typography key={currency} variant="body2">
                {formatCurrency(amount, currency)}
              </Typography>
            ))}
          </Box>
        );
      }
      return JSON.stringify(value);
    }
    // Handle currency amounts
    if (
      fieldName &&
      (fieldName.includes("amount") ||
        fieldName.includes("price") ||
        fieldName.includes("total") ||
        fieldName.includes("fees") ||
        fieldName.includes("discount"))
    ) {
      // Get currency from item or default to AFN
      const currency = item.currency || "AFN";
      return (
        <Typography variant="body2">
          {formatCurrency(value, currency)}
        </Typography>
      );
    }
    // Handle date/time fields
    if (
      fieldName &&
      (fieldName.includes("date") ||
        fieldName.includes("time") ||
        fieldName === "created_at" ||
        fieldName === "updated_at")
    ) {
      if (fieldName.includes("time") && !fieldName.includes("date")) {
        return formatTime(value);
      }
      return formatDate(value);
    }
    // Handle status fields with colored chips
    if (
      fieldName &&
      (fieldName.includes("status") ||
        fieldName === "payment_status" ||
        fieldName === "urgency")
    ) {
      let chipColor = "default";
      const stringValue = String(value).toLowerCase();
      if (
        stringValue.includes("complete") ||
        stringValue.includes("paid") ||
        stringValue.includes("success") ||
        stringValue.includes("high")
      ) {
        chipColor = "success";
      } else if (
        stringValue.includes("pending") ||
        stringValue.includes("wait") ||
        stringValue.includes("medium")
      ) {
        chipColor = "warning";
      } else if (
        stringValue.includes("cancel") ||
        stringValue.includes("fail") ||
        stringValue.includes("low")
      ) {
        chipColor = "error";
      }
      return <Chip label={value} size="small" color={chipColor} />;
    }
    // Default string rendering
    return <Typography variant="body2">{value.toString()}</Typography>;
  };
  const getColumns = () => {
    if (!history || history.length === 0) return [];
    // Get all possible keys from history items
    const allKeys = new Set();
    history.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (!["id", "date"].includes(key)) allKeys.add(key);
      });
      if (item.details && typeof item.details === "object") {
        Object.keys(item.details).forEach((key) =>
          allKeys.add(`details.${key}`)
        );
      }
    });
    // Filter out person objects and items arrays as they'll be handled specially
    return Array.from(allKeys).filter(
      (key) =>
        !["patient", "doctor", "assigned_doctor", "items", "drugs"].includes(
          key
        )
    );
  };
  const columns = getColumns();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="history-dialog-title"
    >
      <DialogTitle
        id="history-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          fontWeight: "bold",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <History aria-hidden="true" />
          <span>
            {title} {t("overview.history", "History")}
          </span>
        </Box>
        <IconButton
          onClick={onClose}
          color="inherit"
          aria-label={t("overview.close", "Close")}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {history?.length > 0 ? (
          <TableContainer sx={{ maxHeight: "calc(100vh - 200px)" }}>
            <Table stickyHeader aria-label="History details table" size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                  <TableCell>{t("overview.id", "ID")}</TableCell>
                  <TableCell>{t("overview.date", "Date")}</TableCell>
                  {title === t("overview.payroll", "Payroll") ||
                  title === t("overview.advances", "Advances") ||
                  title === t("overview.bonuses", "Bonuses") ? (
                    <TableCell>{t("overview.employee", "Employee")}</TableCell>
                  ) : (
                    <TableCell>{t("overview.patient", "Patient")}</TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={col}>
                      {t(
                        `overview.${col.replace(".", "_")}`,
                        col.replace(/_/g, " ")
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item, index) => (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: theme.palette.action.hover,
                      },
                      "&:nth-of-type(even)": {
                        backgroundColor: theme.palette.background.paper,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        #{item.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(item.date)}</TableCell>
                    <TableCell>
                      {title === t("overview.payroll", "Payroll") ||
                      title === t("overview.advances", "Advances") ||
                      title === t("overview.bonuses", "Bonuses")
                        ? formatEmployeeInfo(item.employee)
                        : item.patient
                        ? formatEmployeeInfo(item.patient)
                        : item.doctor
                        ? formatEmployeeInfo(item.doctor)
                        : "-"}
                    </TableCell>
                    {columns.map((col) => {
                      const [parent, child] = col.split(".");
                      const value =
                        parent === "details"
                          ? item.details?.[child]
                          : item[col];
                      return (
                        <TableCell key={col}>
                          {renderValue(value, col, item)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            textAlign="center"
          >
            <Description color="disabled" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t("overview.noHistory", "No History Available")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {t(
                "overview.noHistoryDescription",
                "There are no records to display for this section."
              )}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          startIcon={<Close />}
          variant="contained"
        >
          {t("overview.close", "Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
const ActivityOverview = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [option, setOption] = useState("daily");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [historyDialog, setHistoryDialog] = useState({
    open: false,
    history: [],
    title: "",
  });
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => {
    const now = new Date();
    let startDate = "";
    let endDate = format(now, "yyyy-MM-dd'T'HH:mm");
    switch (option) {
      case "daily":
        startDate = format(now, "yyyy-MM-dd'T'00:00");
        break;
      case "weekly":
        startDate = format(subDays(now, 7), "yyyy-MM-dd'T'00:00");
        break;
      case "monthly":
        startDate = format(startOfMonth(now), "yyyy-MM-dd'T'00:00");
        break;
      case "yearly":
        startDate = format(startOfYear(now), "yyyy-MM-dd'T'00:00");
        break;
      case "all":
        // For all time, clear start and end datetime inputs
        startDate = "";
        endDate = "";
        break;
      default:
        break;
    }
    setStartDateTime(startDate);
    setEndDateTime(endDate);
    setIsFiltering(false);
  }, [option]);
  const validateDateRange = () => {
    if (!startDateTime || !endDateTime) return false;
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    return isAfter(end, start);
  };
  const getFetchParams = () => {
    let params = "";
    if (option === "custom") {
      // Custom filter: send start and end datetime, empty type
      if (validateDateRange()) {
        params += `?type=&start_datetime=${startDateTime}&end_datetime=${endDateTime}`;
      } else {
        params += `?type=`;
      }
    } else if (option === "all") {
      // All time: send type=all, no dates
      params += `?type=all`;
    } else {
      // Daily, weekly, monthly, yearly: send type only, no dates
      params += `?type=${option}`;
    }
    return params;
  };
  const defaultReport = {
    medical_services: {
      appointments: { total: 0, count: 0, history: [] },
      prescriptions: { total: 0, count: 0, history: [] },
      medicine_orders: { total: 0, count: 0, history: [] },
      ultrasounds: { total: 0, count: 0, history: [] },
      xrays: { total: 0, count: 0, history: [] },
      echos: { total: 0, count: 0, history: [] },
      laboratory_tests: { total: 0, count: 0, history: [] },
      dental_payments: { total: 0, count: 0, history: [] },
      surgeries: { total: 0, count: 0, history: [] },
      medicine_returns: { total: 0, count: 0, history: [] },
      emergency_payments: { total: 0, count: 0, history: [] },
    },
    special_payments: {
      ht_patient_payments: {
        total: { AFN: 0, USD: 0, EUR: 0 },
        count: 0,
        history: [],
      },
      procedure_payments: {
        total: { AFN: 0, USD: 0, EUR: 0 },
        count: 0,
        history: [],
      },
    },
    expenses: {
      purchase_payments: { total: 0, count: 0, history: [] },
      expenses: { total: 0, count: 0, history: [] },
      payroll: { total: 0, count: 0, history: [] },
      advances: { total: 0, count: 0, history: [] },
      bonuses: { total: 0, count: 0, history: [] },
    },
    summary: {
      total_revenue: 0,
      total_expenses: 0,
      net_income: 0,
    },
  };
  const {
    data: reception_report = defaultReport,
    isLoading,
    isError,
    error,
    isFetching,
  } = useFetchObjects(
    ["reception-reports", option, fetchTrigger],
    `reception-reports/${getFetchParams()}`,
    token
  );
  const handleChangeOption = (event) => {
    setOption(event.target.value);
  };
  const handleApplyCustomDate = () => {
    if (!validateDateRange()) return;
    setOption("custom");
    setIsFiltering(true);
    setFetchTrigger((prev) => prev + 1);
  };
  const handleReset = () => {
    setOption("daily");
    setIsFiltering(false);
    setFetchTrigger((prev) => prev + 1);
  };
  const handleViewHistory = (history, title) => {
    setHistoryDialog({
      open: true,
      history: Array.isArray(history) ? history : [],
      title,
    });
  };
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  const formatMultiCurrencyAmount = (totalsObj) => {
    if (!totalsObj || typeof totalsObj !== "object") return [formatCurrency(0)];
    const currencies = ["AFN", "USD", "EUR"];
    const formattedAmounts = [];
    currencies.forEach((currency) => {
      const amount = totalsObj[currency] || 0;
      if (amount > 0) {
        formattedAmounts.push(formatCurrency(amount, currency));
      }
    });
    return formattedAmounts.length > 0 ? formattedAmounts : [formatCurrency(0)];
  };
  const revenueCards = [
    {
      icon: <CalendarToday />,
      title: t("overview.appointments", "Appointments"),
      value: reception_report?.medical_services?.appointments?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.appointments?.total ?? 0
      ),
      history: reception_report?.medical_services?.appointments?.history || [],
      primary: false,
    },
    {
      icon: <LocalPharmacy />,
      title: t("overview.prescriptions", "Prescriptions"),
      value: reception_report?.medical_services?.prescriptions?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.prescriptions?.total ?? 0
      ),
      history: reception_report?.medical_services?.prescriptions?.history || [],
      primary: false,
    },
    {
      icon: <MedicalServices />,
      title: t("overview.medicineOrders", "Medicine Orders"),
      value: reception_report?.medical_services?.medicine_orders?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.medicine_orders?.total ?? 0
      ),
      history:
        reception_report?.medical_services?.medicine_orders?.history || [],
      primary: false,
    },
    {
      icon: <MedicalServices />,
      title: t("overview.ultrasounds", "Ultrasounds"),
      value: reception_report?.medical_services?.ultrasounds?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.ultrasounds?.total ?? 0
      ),
      history: reception_report?.medical_services?.ultrasounds?.history || [],
      primary: false,
    },
    {
      icon: <MedicalServices />,
      title: t("overview.xrays", "X-Rays"),
      value: reception_report?.medical_services?.xrays?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.xrays?.total ?? 0
      ),
      history: reception_report?.medical_services?.xrays?.history || [],
      primary: false,
    },
    {
      icon: <MedicalServices />,
      title: t("overview.ipd", "IPD"),
      value: reception_report?.medical_services?.ipd?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.ipd?.total ?? 0
      ),
      history: reception_report?.medical_services?.ipd?.history || [],
      primary: false,
    },
    {
      icon: <Healing />,
      title: t("overview.icu", "ICU"),
      value: reception_report?.medical_services?.icu?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.icu?.total ?? 0
      ),
      history: reception_report?.medical_services?.icu?.history || [],
      primary: false,
    },
    {
      icon: <Favorite />,
      title: t("overview.echos", "Echos"),
      value: reception_report?.medical_services?.echos?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.echos?.total ?? 0
      ),
      history: reception_report?.medical_services?.echos?.history || [],
      primary: false,
    },
    {
      icon: <Science />,
      title: t("overview.laboratoryTests", "Laboratory Tests"),
      value: reception_report?.medical_services?.laboratory_tests?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.laboratory_tests?.total ?? 0
      ),
      history:
        reception_report?.medical_services?.laboratory_tests?.history || [],
      primary: false,
    },
    {
      icon: <Healing />,
      title: t("overview.surgeries", "Surgeries"),
      value: reception_report?.medical_services?.surgeries?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.surgeries?.total ?? 0
      ),
      history: reception_report?.medical_services?.surgeries?.history || [],
      primary: false,
    },
    {
      icon: <Undo />,
      title: t("overview.medicineReturns", "Medicine Returns"),
      value: reception_report?.medical_services?.medicine_returns?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.medicine_returns?.total ?? 0
      ),
      history:
        reception_report?.medical_services?.medicine_returns?.history || [],
      primary: false,
    },
    {
      icon: <AccountBalanceWallet />,
      title: t("overview.htPatientPayments", "HT Patient Payments").replace(
        /\s*\(.*?\)\s*/g,
        ""
      ),
      value:
        reception_report?.special_payments?.ht_patient_payments?.count ?? 0,
      amount: formatMultiCurrencyAmount(
        reception_report?.special_payments?.ht_patient_payments?.total
      ),
      history:
        reception_report?.special_payments?.ht_patient_payments?.history || [],
      primary: false,
    },
    {
      icon: <Build />,
      title: t("overview.procedures", "Procedures"),
      value: reception_report?.special_payments?.procedure_payments?.count ?? 0,
      amount: formatMultiCurrencyAmount(
        reception_report?.special_payments?.procedure_payments?.total
      ),
      history:
        reception_report?.special_payments?.procedure_payments?.history || [],
      primary: false,
    },
    {
      icon: <MedicalServices />,
      title: t("overview.dentalPayments", "Dental Payments"),
      value: reception_report?.medical_services?.dental_payments?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.dental_payments?.total ?? 0
      ),
      history:
        reception_report?.medical_services?.dental_payments?.history || [],
      primary: false,
    },
    {
      icon: <MedicalServices />,
      title: t("overview.emergencyPayments", "Emergency Payments"),
      value: reception_report?.medical_services?.emergency_payments?.count ?? 0,
      amount: formatCurrency(
        reception_report?.medical_services?.emergency_payments?.total ?? 0
      ),
      history:
        reception_report?.medical_services?.emergency_payments?.history || [],
      primary: false,
    },
  ];
  const expenseCards = [
    {
      icon: <PointOfSale />,
      title: t("overview.purchasePayments", "Purchase Payments"),
      value: reception_report?.expenses?.purchase_payments?.count ?? 0,
      amount: formatCurrency(
        reception_report?.expenses?.purchase_payments?.total ?? 0
      ),
      history: reception_report?.expenses?.purchase_payments?.history || [],
      primary: false,
    },
    {
      icon: <Payments />,
      title: t("overview.expenses", "Expenses"),
      value: reception_report?.expenses?.expenses?.count ?? 0,
      amount: formatCurrency(reception_report?.expenses?.expenses?.total ?? 0),
      history: reception_report?.expenses?.expenses?.history || [],
      primary: false,
    },
    {
      icon: <AccountTree />,
      title: t("overview.payroll", "Payroll"),
      value: reception_report?.expenses?.payroll?.count ?? 0,
      amount: formatCurrency(reception_report?.expenses?.payroll?.total ?? 0),
      history: reception_report?.expenses?.payroll?.history || [],
      primary: false,
    },
    {
      icon: <MoneyOff />,
      title: t("overview.advances", "Advances"),
      value: reception_report?.expenses?.advances?.count ?? 0,
      amount: formatCurrency(reception_report?.expenses?.advances?.total ?? 0),
      history: reception_report?.expenses?.advances?.history || [],
      primary: false,
    },
    {
      icon: <Savings />,
      title: t("overview.bonuses", "Bonuses"),
      value: reception_report?.expenses?.bonuses?.count ?? 0,
      amount: formatCurrency(reception_report?.expenses?.bonuses?.total ?? 0),
      history: reception_report?.expenses?.bonuses?.history || [],
      primary: false,
    },
  ];
  // --- SUM USD/EUR FROM SPECIAL PAYMENTS ---
  const getSpecialPaymentsCurrencyTotals = (special_payments) => {
    const currencyTotals = { USD: 0, EUR: 0 };
    if (!special_payments) return currencyTotals;
    ["ht_patient_payments", "procedure_payments"].forEach((key) => {
      const payment = special_payments[key];
      if (payment && payment.total && typeof payment.total === "object") {
        Object.entries(payment.total).forEach(([currency, value]) => {
          if (currency === "USD" || currency === "EUR") {
            currencyTotals[currency] += Number(value) || 0;
          }
        });
      }
    });
    return currencyTotals;
  };
  // --- SUMMARY CARDS WITH MULTI-CURRENCY ---
  const specialTotals = getSpecialPaymentsCurrencyTotals(
    reception_report?.special_payments
  );
  const summaryCards = [
    {
      icon: <ShowChart />,
      title: t("overview.totalRevenue", "Total Revenue"),
      value: "",
      amount: [
        formatCurrencyWithSpace(
          reception_report?.summary?.total_revenue ?? 0,
          "AFN"
        ),
        specialTotals.USD > 0 &&
          formatCurrencyWithSpace(specialTotals.USD, "USD"),
        specialTotals.EUR > 0 &&
          formatCurrencyWithSpace(specialTotals.EUR, "EUR"),
      ].filter(Boolean),
      highlight: "positive",
      history: [],
      primary: false,
      cardSx: {
        bgcolor: theme.palette.success.main,
        color: "#fff",
        borderLeft: `4px solid ${theme.palette.success.dark}`,
      },
    },
    {
      icon: <MoneyOff />,
      title: t("overview.totalExpenses", "Total Expenses"),
      value: "",
      amount: [
        formatCurrencyWithSpace(
          reception_report?.summary?.total_expenses ?? 0,
          "AFN"
        ),
      ],
      highlight: "negative",
      history: [],
      primary: false,
      cardSx: {
        bgcolor: theme.palette.error.main,
        color: "#fff",
        borderLeft: `4px solid ${theme.palette.error.dark}`,
      },
    },
    {
      icon: <TrendingUp />,
      title: t("overview.netIncome", "Net Income"),
      value: "",
      amount: [
        formatCurrencyWithSpace(
          reception_report?.summary?.net_income ?? 0,
          "AFN"
        ),
        specialTotals.USD > 0 &&
          formatCurrencyWithSpace(specialTotals.USD, "USD"),
        specialTotals.EUR > 0 &&
          formatCurrencyWithSpace(specialTotals.EUR, "EUR"),
      ].filter(Boolean),
      highlight:
        reception_report?.summary?.net_income < 0 ? "negative" : "positive",
      history: [],
      primary: false,
      cardSx: {
        bgcolor: theme.palette.info.main,
        color: "#fff",
        borderLeft: `4px solid ${theme.palette.info.dark}`,
      },
    },
  ];
  const showLoading = isLoading || (isFiltering && isFetching);
  return (
    <Paper
      sx={{
        width: "100%",
        borderRadius: "12px",
        p: { xs: 1, sm: 3 },
        overflow: "hidden",
      }}
    >
      {/* Header Section */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        mb={4}
        gap={2}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ color: theme.palette.text.primary }}
        >
          {t("overview.financialDashboard", "Financial Dashboard")}
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl
            size="small"
            sx={{ minWidth: 120 }}
            disabled={isFetching}
          >
            <InputLabel>{t("overview.timePeriod", "Time Period")}</InputLabel>
            <Select
              value={option}
              onChange={handleChangeOption}
              label={t("overview.timePeriod", "Time Period")}
            >
              <MenuItem value="daily">
                <Box display="flex" alignItems="center" gap={1}>
                  <Today fontSize="small" />
                  {t("overview.daily", "Daily")}
                </Box>
              </MenuItem>
              <MenuItem value="weekly">
                <Box display="flex" alignItems="center" gap={1}>
                  <DateRange fontSize="small" />
                  {t("overview.weekly", "Weekly")}
                </Box>
              </MenuItem>
              <MenuItem value="monthly">
                <Box display="flex" alignItems="center" gap={1}>
                  <DateRange fontSize="small" />
                  {t("overview.monthly", "Monthly")}
                </Box>
              </MenuItem>
              <MenuItem value="yearly">
                <Box display="flex" alignItems="center" gap={1}>
                  <DateRange fontSize="small" />
                  {t("overview.yearly", "Yearly")}
                </Box>
              </MenuItem>
              <MenuItem value="all">
                <Box display="flex" alignItems="center" gap={1}>
                  <AllInclusive fontSize="small" />
                  {t("overview.allTime", "All Time")}
                </Box>
              </MenuItem>
              <MenuItem value="custom">
                <Box display="flex" alignItems="center" gap={1}>
                  <FilterList fontSize="small" />
                  {t("overview.custom", "Custom")}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          {isFiltering && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              disabled={isFetching}
              startIcon={<SettingsBackupRestore />}
            >
              {t("overview.reset", "Reset")}
            </Button>
          )}
        </Box>
      </Box>
      {/* Date Filter Section */}
      {option === "custom" && (
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          gap={2}
          mb={4}
          p={2}
          bgcolor="grey.100"
          borderRadius="8px"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <FilterList color="action" />
            <Typography variant="body2" color="text.secondary">
              {t("overview.customDateRange", "Custom Date Range")}
            </Typography>
          </Box>
          <TextField
            type="datetime-local"
            label={t("overview.startDateTime", "Start Date & Time")}
            value={startDateTime}
            onChange={(e) => setStartDateTime(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            disabled={isFetching}
            sx={{ flex: 1, maxWidth: 250 }}
            error={!validateDateRange() && startDateTime && endDateTime}
            helperText={
              !validateDateRange() && startDateTime && endDateTime
                ? t("overview.invalidDateRange", "Invalid date range")
                : ""
            }
          />
          <TextField
            type="datetime-local"
            label={t("overview.endDateTime", "End Date & Time")}
            value={endDateTime}
            onChange={(e) => setEndDateTime(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            disabled={isFetching}
            sx={{ flex: 1, maxWidth: 250 }}
          />
          <Button
            variant="contained"
            onClick={handleApplyCustomDate}
            disabled={!validateDateRange() || isFetching}
            startIcon={
              isFetching ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Event />
              )
            }
            sx={{ minWidth: 120 }}
          >
            {t("overview.apply", "Apply")}
          </Button>
        </Box>
      )}
      {/* Current Filter Display */}
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          {t("overview.currentFilter", "Current Filter")}:{" "}
          <strong>
            {option === "custom"
              ? `${t("overview.custom", "Custom")} (${
                  startDateTime ? format(new Date(startDateTime), "PPpp") : ""
                } - ${
                  endDateTime ? format(new Date(endDateTime), "PPpp") : ""
                })`
              : t(
                  `overview.${option}`,
                  option.charAt(0).toUpperCase() + option.slice(1)
                )}
          </strong>
        </Typography>
      </Box>
      {/* Error State */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error?.message || t("overview.fetchError", "Failed to fetch data")}
        </Alert>
      )}
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab
            label={t("overview.revenue", "Revenue")}
            icon={<Receipt />}
            iconPosition="start"
          />
          <Tab
            label={t("overview.expenses", "Expenses")}
            icon={<MoneyOff />}
            iconPosition="start"
          />
          <Tab
            label={t("overview.summary", "Summary")}
            icon={<Summarize />}
            iconPosition="start"
          />
        </Tabs>
      </Box>
      {/* Revenue Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {showLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                  <StatCard loading />
                </Grid>
              ))
            : revenueCards.map((item, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                  <StatCard
                    icon={item.icon}
                    title={item.title}
                    value={item.value}
                    amount={item.amount}
                    onClick={() => handleViewHistory(item.history, item.title)}
                    theme={theme}
                    history={item.history}
                    highlight={item.highlight}
                  />
                </Grid>
              ))}
        </Grid>
      )}
      {/* Expenses Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {showLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                  <StatCard loading />
                </Grid>
              ))
            : expenseCards.map((item, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                  <StatCard
                    icon={item.icon}
                    title={item.title}
                    value={item.value}
                    amount={item.amount}
                    onClick={() => handleViewHistory(item.history, item.title)}
                    theme={theme}
                    history={item.history}
                    highlight={item.highlight}
                  />
                </Grid>
              ))}
        </Grid>
      )}
      {/* Summary Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          {showLoading
            ? Array.from({ length: 3 }).map((_, index) => (
                <Grid key={index} item xs={12} sm={6} md={4}>
                  <StatCard loading />
                </Grid>
              ))
            : summaryCards.map((item, index) => (
                <Grid key={index} item xs={12} sm={6} md={4}>
                  <StatCard
                    icon={item.icon}
                    title={item.title}
                    value={item.value}
                    amount={item.amount}
                    highlight={item.highlight}
                    onClick={() => handleViewHistory(item.history, item.title)}
                    theme={theme}
                    history={item.history}
                    cardSx={item.cardSx}
                  />
                </Grid>
              ))}
        </Grid>
      )}
      {/* Loading Indicator */}
      {isFetching && !showLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
          <CircularProgress />
        </Box>
      )}
      {/* History Dialog */}
      <HistoryDialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ ...historyDialog, open: false })}
        history={historyDialog.history}
        title={historyDialog.title}
      />
    </Paper>
  );
};
export default ActivityOverview;
